import './index.scss';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { userInfo } from '../../../redux/slice';
import { useSelector } from 'react-redux';
import { get } from '../../../util/request';
import Loading from '../../../components/common/loading.js';
import { useTranslation } from 'react-i18next';
import { getLocalizedContent, getQuestionnaireStatus, getShortfallStatus, getVisibleQuestionnaires, hasOutstandingShortfall } from '../taxQuestionnaireRules';
import useTaxContent from '../useTaxContent';

export default Questionnaires; function Questionnaires() {
    const history = useHistory();
    const user = useSelector(userInfo);
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();
    const previewDetail = JSON.parse(sessionStorage.getItem('previewDetail'));
    // Configurable via the "State Tax Warning" content row; falls back to the i18n placeholder.
    const stateTaxWarning = getLocalizedContent(useTaxContent('State Tax Warning'), window.localStorage.lang);

    function goDetail(url, year, action) {
        history.push({
            pathname: url
        });
        sessionStorage.setItem('taxYear', year);
        sessionStorage.setItem('taxAction', action);
    }

    const [questionnaires, setQuestionnaires] = useState([]);
    const [showAll, setShowAll] = useState(false);

    const openQuestionnaire = (event, year, action) => {
        if (!user.accessToken) {
            event.preventDefault();
        }
        else {
            goDetail(user.sfUserType === 'IR' ? '/preview/taxQuestionnaire' : '/taxQuestionnaire', year, action);
        }
    }

    const isPayEligible = (questionnaire) => !(questionnaire.CountryOfTaxResidency === 'USA' || questionnaire.OtherUSIncomeSource === 'Yes'
        || questionnaire.ActiveGreenCardHolder === 'Yes' || questionnaire.ActiveUSCitizen === 'Yes' || (questionnaire.CountryOfNationality === 'United States' || questionnaire.CountryOfNationality === 'USA')
        || (((questionnaire.PeriodStayedInUSCurrentYear + (questionnaire.PeriodStayedInUSLastYear / 3) + (questionnaire.PeriodStayedInUSTwoYearsAgo / 6) > 183) && questionnaire.PeriodStayedInUSCurrentYear > 30)));

    const showPayButton = (questionnaire) => isPayEligible(questionnaire) && hasOutstandingShortfall(questionnaire);

    // Default view shows the current tax filing year plus earlier years still Pending Payment; Show All reveals the rest.
    const visibleQuestionnaires = getVisibleQuestionnaires(questionnaires, showAll);
    const hiddenCount = (questionnaires?.length ?? 0) - visibleQuestionnaires.length;

    useEffect(() => {
      const pd = JSON.parse(sessionStorage.getItem('previewDetail'));
      const isClientView = (pd !== null && pd?.sfUserType === 'CL') ||
        (pd === null && user?.sfUserType === 'CL');
      const hasActiveInv = pd !== null ? pd?.hasActiveInvestment : user?.hasActiveInvestment;
      if (isClientView && hasActiveInv === false) {
        history.push(history.location.pathname.startsWith('/preview/') ? '/preview/document' : '/document');
      }
    }, []);

    const init = async () => {
        setLoading(true);
        const data = await get('/taxQuestionnaire/list', {
            accessToken: user.accessToken,
            userId: user.userId
        });
        if (data) {

            data?.map((stay, index) => {
                let currentYear = 0;
                let totalDaysLastYear = 0;
                let totalDaysLast2Years = 0;

                stay?.USStays?.map((item) => {
                    const year = getDaysPerYear(item.FromDate, item.ToDate);
                    currentYear = currentYear + (year[data[index].TaxFilingYear] ?? 0);
                    totalDaysLastYear = totalDaysLastYear + (year[data[index].TaxFilingYear - 1] ?? 0);
                    totalDaysLast2Years = totalDaysLast2Years + (year[data[index].TaxFilingYear - 2] ?? 0);
                });



                data[index].PeriodStayedInUSTwoYearsAgo = totalDaysLast2Years;
                data[index].PeriodStayedInUSLastYear = totalDaysLastYear;
                data[index].PeriodStayedInUSCurrentYear = currentYear;
            });

            setQuestionnaires(data);
            setLoading(false);
        }
    }

    function getDaysPerYear(startDate, endDate) {
        const result = {};
        const start = new Date(startDate);
        const end = new Date(endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        const endExclusive = new Date(end.getTime() + 24 * 60 * 60 * 1000);

        const startYear = start.getFullYear();
        const endYear = end.getFullYear();

        for (let year = startYear; year <= endYear; year++) {
            const yearStart = new Date(year, 0, 1);
            const yearEndExclusive = new Date(year + 1, 0, 1);
            const rangeStart = new Date(Math.max(start.getTime(), yearStart.getTime()));
            const rangeEnd = new Date(Math.min(endExclusive.getTime(), yearEndExclusive.getTime()));

            if (rangeEnd > rangeStart) {
                const days = (rangeEnd - rangeStart) / (24 * 60 * 60 * 1000);
                result[year] = days;
            }
        }
        return result;
    }

    const formatDate = (value) =>
        new Intl.DateTimeFormat('fr-CA', {
            year: 'numeric', month: '2-digit', day: '2-digit'

        }).format(value).replace(/-/g, '/');

    useEffect(() => {
        init();
    }, []);

    return (<>
        {
            ((user.sfUserType === 'CL' && user.hasTaxQuestionnaire) || (previewDetail?.sfUserType === 'CL' && previewDetail?.hasTaxQuestionnaire)) && (<>
                {loading && <Loading />}
                <div id='questionnaires'>
                    <div className='main-box questionnaire-div'>
                        <div className="headline tax-wrapper">
                            <div className={'tax-title'}><p>{t('Tax Questionnaires')}</p></div>
                        </div>

                        <div className='submit-box tax-container'>
                            <div className='col-md-12 row input-row'>
                                <div className='col-md-12 tax-filing-questionnaire-notes'>
                                    <svg
                                        className="svg-circle"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                    >
                                        <path
                                            d="M10 20C4.48674 20 0 15.5054 0 9.97817C0 4.45098 4.48674 0 10 0C15.5133 0 20 4.47756 20 9.97817C20 15.4788 15.5133 20 10 20ZM10 1.84493C5.50189 1.84493 1.84091 5.49302 1.84091 9.97817C1.84091 14.4633 5.50189 18.1551 10 18.1551C14.4981 18.1551 18.1591 14.488 18.1591 9.97817C18.1591 5.46835 14.5 1.84493 10 1.84493Z"
                                            fill="#003448"
                                        />
                                    </svg>
                                    <svg
                                        className="svg-exclamation"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="2"
                                        height="10"
                                        viewBox="0 0 2 10"
                                        fill="none"
                                    >
                                        <path
                                            d="M0.918557 9.50633C0.412028 9.50633 0 9.09255 0 8.58387V4.72129C0 4.21261 0.412028 3.79883 0.918557 3.79883C1.42509 3.79883 1.83711 4.21261 1.83711 4.72129V8.58387C1.83711 9.09255 1.42509 9.50633 0.918557 9.50633Z"
                                            fill="#003448"
                                        />
                                        <path
                                            d="M0.918557 2.89808C0.412028 2.89808 0 2.4843 0 1.97561V1.41758C0 0.908897 0.412028 0.495117 0.918557 0.495117C1.42509 0.495117 1.83711 0.908897 1.83711 1.41758V1.97561C1.83711 2.4843 1.42509 2.89808 0.918557 2.89808Z"
                                            fill="#003448"
                                        />
                                    </svg>
                                    <span>{t('Please proceed with View to fill out the AAS Questionnaire, then before submission you will be able to see the available payment options to Pay the applicable AAS Shortfall Fee.')}</span>
                                </div>
                            </div>

                            <div className='col-md-12 row input-row'>
                                <div className='col-md-12 tax-filing-questionnaire-notes'>
                                    <svg
                                        className="svg-circle"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                    >
                                        <path
                                            d="M10 20C4.48674 20 0 15.5054 0 9.97817C0 4.45098 4.48674 0 10 0C15.5133 0 20 4.47756 20 9.97817C20 15.4788 15.5133 20 10 20ZM10 1.84493C5.50189 1.84493 1.84091 5.49302 1.84091 9.97817C1.84091 14.4633 5.50189 18.1551 10 18.1551C14.4981 18.1551 18.1591 14.488 18.1591 9.97817C18.1591 5.46835 14.5 1.84493 10 1.84493Z"
                                            fill="#003448"
                                        />
                                    </svg>
                                    <svg
                                        className="svg-exclamation"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="2"
                                        height="10"
                                        viewBox="0 0 2 10"
                                        fill="none"
                                    >
                                        <path
                                            d="M0.918557 9.50633C0.412028 9.50633 0 9.09255 0 8.58387V4.72129C0 4.21261 0.412028 3.79883 0.918557 3.79883C1.42509 3.79883 1.83711 4.21261 1.83711 4.72129V8.58387C1.83711 9.09255 1.42509 9.50633 0.918557 9.50633Z"
                                            fill="#003448"
                                        />
                                        <path
                                            d="M0.918557 2.89808C0.412028 2.89808 0 2.4843 0 1.97561V1.41758C0 0.908897 0.412028 0.495117 0.918557 0.495117C1.42509 0.495117 1.83711 0.908897 1.83711 1.41758V1.97561C1.83711 2.4843 1.42509 2.89808 0.918557 2.89808Z"
                                            fill="#003448"
                                        />
                                    </svg>
                                    {
                                        stateTaxWarning ? <span dangerouslySetInnerHTML={{ __html: stateTaxWarning }} />
                                            : <span>{t('State_Tax_Warning_Placeholder')}</span>
                                    }
                                </div>
                            </div>

                            <div className='table-responsive'>
                                <table className='questionnaire-table'>
                                    <thead>
                                        <tr>
                                            <th>{t('Tax Filing Year')}</th>
                                            <th>{t('Due Date')}</th>
                                            <th>{t('Submission Date')}</th>
                                            <th>{t('AAS Questionnaire Status')}</th>
                                            <th>{t('Shortfall Status')}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            visibleQuestionnaires.map((questionnaire) => {
                                                const status = getQuestionnaireStatus(questionnaire);
                                                const shortfallStatus = getShortfallStatus(questionnaire);
                                                return (
                                                    <tr key={questionnaire.TaxFilingYear}>
                                                        <td data-label={t('Tax Filing Year')} className='year-cell'>{questionnaire.TaxFilingYear}</td>
                                                        <td data-label={t('Due Date')}>
                                                            {questionnaire?.DueDate.includes('1900') ? '-' : formatDate(new Date(questionnaire?.DueDate))}
                                                        </td>
                                                        <td data-label={t('Submission Date')}>
                                                            {questionnaire?.SubmissionDate.includes('1900') ? '-' : formatDate(new Date(questionnaire?.SubmissionDate))}
                                                        </td>
                                                        <td data-label={t('AAS Questionnaire Status')}>
                                                            <span className={status.pending ? 'pending-action' : ''}>{t(status.label)}</span>
                                                        </td>
                                                        <td data-label={t('Shortfall Status')}>
                                                            <span className={shortfallStatus.pending ? 'pending-action' : ''}>{t(shortfallStatus.label)}</span>
                                                        </td>
                                                        <td className='button-cell' data-label=''>
                                                            <button type='button' className='action-button' onClick={(event) => openQuestionnaire(event, questionnaire?.TaxFilingYear, 'View')}>{t('View')}</button>
                                                            {
                                                                showPayButton(questionnaire) && (
                                                                    <button type='button' className='action-button' onClick={(event) => openQuestionnaire(event, questionnaire?.TaxFilingYear, 'Pay')}>{t('Pay')}</button>
                                                                )
                                                            }
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        }
                                    </tbody>
                                </table>
                            </div>
                            {
                                (showAll || hiddenCount > 0) && (
                                    <div className='show-more-row'>
                                        <button type='button' className='show-more-button' onClick={() => setShowAll((prev) => !prev)}>
                                            {showAll ? t('Show Less') : `${t('Show All')} (${hiddenCount})`}
                                            <svg className={showAll ? 'is-open' : ''} width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                )
                            }
                        </div>
                    </div>
                </div>
            </>)
        }
    </>);
}